import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit, Trash2, X, Upload, Loader } from 'lucide-react';
import {
  fetchGalleryImages,
  createGalleryImage,
  updateGalleryImage,
  deleteGalleryImage
} from '../../lib/sanityClient';
import ConfirmDialog from './ConfirmDialog';
import SuccessToast from './SuccessToast';
import ErrorAlert from './ErrorAlert';

export default function AdminGallery({ refreshTrigger = 0 }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, id: null });
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    imageFile: null
  });
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    loadImages();
  }, [refreshTrigger]);

  const loadImages = async () => {
    try {
      setLoading(true);
      const data = await fetchGalleryImages();
      setImages(data || []);
      setError('');
    } catch (err) {
      setError('Kunne ikke laste galleribilder');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingId(null);
    setImagePreview(null);
    setFormData({
      title: '',
      description: '',
      imageFile: null
    });
    setShowForm(true);
  };

  const handleEdit = (image) => {
    setEditingId(image._id);
    setImagePreview(image.image?.asset?.url || null);
    setFormData({
      title: image.title,
      description: image.description || '',
      imageFile: null
    });
    setShowForm(true);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file
      if (!file.type.startsWith('image/')) {
        setError('Filen må være et bilde');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setError('Bildet er for stort. Maks 5MB.');
        return;
      }

      // Show preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);

      setFormData({ ...formData, imageFile: file });
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);

    try {
      // Validate
      if (!formData.title.trim()) {
        setError('Tittel må fylles ut');
        setSubmitLoading(false);
        return;
      }

      if (!editingId && !formData.imageFile) {
        setError('Du må velge et bilde');
        setSubmitLoading(false);
        return;
      }

      if (editingId) {
        await updateGalleryImage(editingId, formData, formData.imageFile);
        setSuccessMessage('Bilde oppdatert!');
      } else {
        await createGalleryImage(formData, formData.imageFile);
        setSuccessMessage('Bilde lastet opp!');
      }

      setShowForm(false);
      await loadImages();
      setError('');
    } catch (err) {
      setError(err.message || 'En feil oppstod');
      console.error(err);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setSubmitLoading(true);
      await deleteGalleryImage(confirmDialog.id);
      setSuccessMessage('Bilde slettet!');
      setConfirmDialog({ isOpen: false, id: null });
      await loadImages();
    } catch (err) {
      setError(err.message || 'Kunne ikke slette bilde');
      console.error(err);
    } finally {
      setSubmitLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-Pink-main"></div>
      </div>
    );
  }

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible">
      <ErrorAlert message={error} onClose={() => setError('')} />

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h2 className="text-xl sm:text-2xl font-semibold text-Neautral-main font-opensans">
          Galleri ({images.length})
        </h2>
        <motion.button
          onClick={handleAddNew}
          className="flex items-center justify-center gap-2 px-6 py-3 rounded-[15px] bg-Pink-main text-white font-semibold font-opensans hover:opacity-90 transition w-full sm:w-auto"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus size={20} strokeWidth={2} />
          Nytt Bilde
        </motion.button>
      </div>

      {/* Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            className="mb-8 bg-Pink-BG rounded-[15px] p-4 sm:p-6 md:p-8 border-2 border-Pink-main"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            variants={itemVariants}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-Neautral-main font-opensans">
                {editingId ? 'Rediger Bilde' : 'Last opp Bilde'}
              </h3>
              <motion.button
                onClick={() => setShowForm(false)}
                className="text-Neautral-light hover:text-Neautral-main transition"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <X size={24} strokeWidth={2} />
              </motion.button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-semibold text-Neautral-main font-opensans mb-2">
                  {editingId ? 'Velg nytt bilde (valgfritt)' : 'Velg bilde'} *
                </label>
                <div className="space-y-4">
                  {imagePreview && (
                    <div className="relative w-full max-w-sm">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full rounded-[10px] border-2 border-Neautral-base object-cover max-h-48 sm:max-h-64"
                      />
                      {editingId && (
                        <motion.button
                          type="button"
                          onClick={() => {
                            setImagePreview(null);
                            setFormData({ ...formData, imageFile: null });
                          }}
                          className="absolute top-2 right-2 p-2 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <X size={18} strokeWidth={2} />
                        </motion.button>
                      )}
                    </div>
                  )}

                  <label className="flex flex-col items-center justify-center gap-2 px-4 py-4 sm:py-6 rounded-[10px] border-2 border-dashed border-Neautral-base bg-Neautral-white cursor-pointer hover:border-Pink-main hover:bg-Pink-BG transition">
                    <Upload size={20} className="sm:block text-Neautral-light" strokeWidth={1.5} />
                    <div className="text-center">
                      <p className="text-xs sm:text-sm font-semibold text-Neautral-main font-opensans">
                        Klikk for å velge eller dra bilde her
                      </p>
                      <p className="text-xs text-Neautral-light font-opensans">
                        JPG, PNG, WebP (maks 5MB)
                      </p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-Neautral-main font-opensans mb-2">
                  Tittel *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Bildetittel"
                  className="w-full px-4 py-2 rounded-[10px] border-2 border-Neautral-base bg-Neautral-white text-Neautral-main placeholder-Neautral-light focus:outline-none focus:border-Pink-main transition font-opensans"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-Neautral-main font-opensans mb-2">
                  Beskrivelse
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Valfri beskrivelse av bildet"
                  rows="3"
                  className="w-full px-4 py-2 rounded-[10px] border-2 border-Neautral-base bg-Neautral-white text-Neautral-main placeholder-Neautral-light focus:outline-none focus:border-Pink-main transition font-opensans resize-none"
                />
              </div>

              {/* Buttons */}
              <div className="flex flex-col-reverse sm:flex-row gap-4 justify-end pt-4">
                <motion.button
                  type="button"
                  onClick={() => setShowForm(false)}
                  disabled={submitLoading}
                  className="px-6 py-2 rounded-[10px] bg-Neautral-base text-Neautral-main font-semibold font-opensans hover:bg-Neautral-light transition disabled:opacity-50"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Avbryt
                </motion.button>
                <motion.button
                  type="submit"
                  disabled={submitLoading}
                  className="px-6 py-2 rounded-[10px] bg-Pink-main text-white font-semibold font-opensans hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {submitLoading ? (
                    <>
                      <Loader size={18} className="animate-spin" />
                      <span className="hidden sm:inline">Laster opp...</span>
                      <span className="sm:hidden">Laster...</span>
                    </>
                  ) : editingId ? (
                    'Oppdater'
                  ) : (
                    'Last opp'
                  )}
                </motion.button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Gallery Grid */}
      {images.length === 0 ? (
        <motion.div
          className="text-center py-12 text-Neautral-light font-opensans"
          variants={itemVariants}
        >
          Ingen bilder i galleriet ennå. Klikk "Nytt Bilde" for å legge til ett.
        </motion.div>
      ) : (
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
          variants={containerVariants}
        >
          {images.map((image) => (
            <motion.div
              key={image._id}
              className="bg-Neautral-white rounded-[15px] overflow-hidden shadow-[0px_10px_25px_0px_rgba(126,126,126,0.15)] hover:shadow-[0px_25px_55px_0px_rgba(126,126,126,0.25)] transition"
              variants={itemVariants}
            >
              {/* Image */}
              {image.image?.asset?.url && (
                <div className="relative w-full bg-Neautral-base overflow-hidden aspect-video">
                  <img
                    src={image.image.asset.url}
                    alt={image.title}
                    className="w-full h-full object-cover hover:scale-105 transition duration-300"
                  />
                </div>
              )}

              {/* Content */}
              <div className="p-3 sm:p-4">
                <h3 className="text-sm sm:text-lg font-semibold text-Neautral-main font-opensans mb-2 line-clamp-1">
                  {image.title}
                </h3>
                {image.description && (
                  <p className="text-xs sm:text-sm text-Neautral-light font-opensans mb-3 sm:mb-4 line-clamp-2">
                    {image.description}
                  </p>
                )}

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-2">
                  <motion.button
                    onClick={() => handleEdit(image)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-[10px] bg-Neautral-base text-Neautral-main hover:bg-Pink-main hover:text-white transition text-xs sm:text-sm font-semibold font-opensans"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Edit size={14} strokeWidth={2} className="sm:hidden" />
                    <Edit size={16} strokeWidth={2} className="hidden sm:block" />
                    Rediger
                  </motion.button>
                  <motion.button
                    onClick={() => setConfirmDialog({ isOpen: true, id: image._id })}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-[10px] bg-red-100 text-red-600 hover:bg-red-200 transition text-xs sm:text-sm font-semibold font-opensans"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Trash2 size={14} strokeWidth={2} className="sm:hidden" />
                    <Trash2 size={16} strokeWidth={2} className="hidden sm:block" />
                    Slett
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title="Slett bilde?"
        message="Er du sikker på at du vil slette dette bildet? Denne handlingen kan ikke angres."
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDialog({ isOpen: false, id: null })}
        isLoading={submitLoading}
      />

      {/* Success Toast */}
      <SuccessToast
        message={successMessage}
        isVisible={!!successMessage}
        onClose={() => setSuccessMessage('')}
      />
    </motion.div>
  );
}
